import { useState } from "react";
import { PanelLeftClose } from "@repo/ui";
import { ResizeHandleOnMouseDown } from "~/components/ui/resize-handle-on-mouse-down";
import { Title } from "~/components/ui/title";
import { useResizableWidth } from "~/hooks/use-resizable-width";
import type { Item, Task } from "~/types/api";
import { ItemLogView } from "./log-view/item-log-view";
import { ItemMetaView } from "./meta-view/item-meta-view";
import { ItemExplorerToolbar } from "./toolbar/item-explorer-toolbar";
import type { StatusGroup, TabWindow } from "../../task.types";

type Props = {
  selectedItems: Item[];
  tasks?: Task[];
  isLoading?: boolean;
  onClose: () => void;
  window: TabWindow<StatusGroup>;
};

export type FieldViewType = "log" | "meta";

const MIN_WIDTH = 320;
const MAX_WIDTH = 1200;
const DEFAULT_WIDTH = 672;

export const ItemDetailView = ({
  selectedItems,
  isLoading = false,
  onClose,
  window: tabWindow,
  tasks,
}: Props) => {
  const [activeView, setActiveView] = useState<FieldViewType>("log");
  const { width, onResizeStart } = useResizableWidth({
    min: MIN_WIDTH,
    max: MAX_WIDTH,
    initial: DEFAULT_WIDTH,
  });

  const handleViewChange = (view: FieldViewType) => {
    setActiveView(view);
  };

  const count = selectedItems?.length ?? 0;
  const labelTitle = count === 1 ? selectedItems?.[0]?.name : `${count} selected`;

  return (
    <div
      className="border-gxe-border relative flex h-full shrink-0 flex-col border-r border-l bg-white"
      style={{ width }}
    >
      <ItemExplorerToolbar
        onClose={onClose}
        activeView={activeView}
        onViewChange={handleViewChange}
      />

      {count === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-gray-400">
          <PanelLeftClose className="h-8 w-8 opacity-30" />
          <p className="text-sm">Select an item to view details</p>
        </div>
      ) : (
        <>
          <Title
            headerClassName="border-b border-gray-200 overflow-hidden text-ellipsis whitespace-nowrap"
            text={
              <span>
                {activeView === "log" ? "Log" : "Metadata"}:{" "}
                <span className="font-normal">{labelTitle}</span>
              </span>
            }
          />
          {activeView === "log" ? (
            <ItemLogView items={selectedItems} isLoading={isLoading} />
          ) : (
            <ItemMetaView
              items={selectedItems}
              tasks={tasks}
              isLoading={isLoading}
              window={tabWindow}
            />
          )}
        </>
      )}

      <ResizeHandleOnMouseDown handleResizeMouseDown={onResizeStart} />
    </div>
  );
};
